import { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ShoppingCart, Menu, X, ChevronDown, Search, Package } from "lucide-react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Header = ({ cart, onCartClick }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [categories, setCategories] = useState([]);
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const searchRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  // Fetch categories from API
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await axios.get(`${API}/categories`);
        setCategories(res.data);
      } catch (e) {
        console.error("Error fetching categories:", e);
      }
    };
    fetchCategories();
  }, []);

  // Close search when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowSearch(false);
        setSearchResults([]);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Debounced search
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      setIsSearching(true);
      try {
        const res = await axios.get(`${API}/products?search=${encodeURIComponent(searchQuery.trim())}`);
        setSearchResults(res.data.slice(0, 6)); // Limit to 6 results
      } catch (e) {
        console.error("Search error:", e);
      } finally {
        setIsSearching(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleSearchSelect = (productId) => {
    setShowSearch(false);
    setSearchQuery("");
    setSearchResults([]);
    navigate(`/product/${productId}`);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter" && searchQuery.trim()) {
      setShowSearch(false);
      setSearchResults([]);
      navigate(`/shop?search=${encodeURIComponent(searchQuery.trim())}`);
    }
    if (e.key === "Escape") {
      setShowSearch(false);
      setSearchResults([]);
    }
  };

  const staticLinks = [
    { path: "/about", label: "About" },
    { path: "/contact", label: "Contact" },
  ];

  const cartItemCount = cart?.items?.reduce((sum, item) => sum + item.quantity, 0) || 0;

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className="fixed top-0 left-0 right-0 z-40 glass"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0" data-testid="header-logo">
            <h1 className="font-unbounded text-xl md:text-2xl font-bold gradient-text">
              AIRO VAPOR
            </h1>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-6">
            {/* Shop All Link */}
            <Link
              to="/shop"
              data-testid="nav-shop-all"
              className={`font-outfit text-sm tracking-wider uppercase transition-colors duration-300 ${
                location.pathname === "/shop"
                  ? "text-primary neon-text"
                  : "text-muted-foreground hover:text-primary"
              }`}
            >
              Shop All
            </Link>

            {/* Categories Dropdown */}
            <div 
              className="relative"
              onMouseEnter={() => setShowCategoryDropdown(true)}
              onMouseLeave={() => setShowCategoryDropdown(false)}
            >
              <button
                data-testid="nav-categories"
                className={`font-outfit text-sm tracking-wider uppercase transition-colors duration-300 flex items-center gap-1 ${
                  location.pathname.includes("/shop/")
                    ? "text-primary neon-text"
                    : "text-muted-foreground hover:text-primary"
                }`}
              >
                Categories
                <ChevronDown className={`w-4 h-4 transition-transform ${showCategoryDropdown ? 'rotate-180' : ''}`} />
              </button>

              {/* Dropdown Menu */}
              {showCategoryDropdown && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  className="absolute top-full left-0 mt-2 w-56 bg-card border border-border rounded-xl shadow-xl overflow-hidden"
                >
                  <div className="py-2">
                    {categories.map((cat, idx) => (
                      <Link
                        key={`${cat.franpos_id || cat.id || idx}-${cat.slug || ''}`}
                        to={`/shop/${cat.slug || cat.id}`}
                        data-testid={`nav-category-${cat.slug || cat.id}`}
                        className={`block px-4 py-3 font-outfit text-sm transition-colors ${
                          location.pathname === `/shop/${cat.slug || cat.id}`
                            ? "bg-primary/10 text-primary"
                            : "text-muted-foreground hover:bg-muted hover:text-foreground"
                        }`}
                      >
                        {cat.name}
                      </Link>
                    ))}
                  </div>
                </motion.div>
              )}
            </div>

            {/* Static Links */}
            {staticLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-${link.label.toLowerCase()}`}
                className={`font-outfit text-sm tracking-wider uppercase transition-colors duration-300 ${
                  location.pathname === link.path
                    ? "text-primary neon-text"
                    : "text-muted-foreground hover:text-primary"
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Cart & Mobile Menu */}
          <div className="flex items-center gap-2 md:gap-4">
            {/* Search Button & Dropdown */}
            <div className="relative" ref={searchRef}>
              <Button
                data-testid="search-btn"
                variant="ghost"
                size="icon"
                onClick={() => setShowSearch(!showSearch)}
                className="hover:bg-muted"
              >
                <Search className="w-5 h-5 text-foreground" />
              </Button>

              {/* Search Dropdown */}
              <AnimatePresence>
                {showSearch && (
                  <motion.div
                    initial={{ opacity: 0, y: -10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -10, scale: 0.95 }}
                    className="absolute right-0 top-full mt-2 w-80 bg-card border border-border rounded-xl shadow-xl overflow-hidden z-50"
                  >
                    <div className="p-3 border-b border-border">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          data-testid="search-input"
                          type="text"
                          placeholder="Search products..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          onKeyDown={handleSearchKeyDown}
                          className="pl-10 bg-muted border-border"
                          autoFocus
                        />
                      </div>
                    </div>

                    {/* Search Results */}
                    <div className="max-h-80 overflow-y-auto">
                      {isSearching ? (
                        <div className="p-4 text-center text-muted-foreground font-outfit text-sm">
                          Searching...
                        </div>
                      ) : searchResults.length > 0 ? (
                        <div className="py-2">
                          {searchResults.map((product) => (
                            <button
                              key={product.id}
                              data-testid={`search-result-${product.id}`}
                              onClick={() => handleSearchSelect(product.id)}
                              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-muted transition-colors text-left"
                            >
                              <img
                                src={product.image_url}
                                alt={product.name}
                                className="w-10 h-10 rounded-lg object-cover bg-muted"
                              />
                              <div className="flex-1 min-w-0">
                                <p className="font-outfit text-sm text-foreground truncate">
                                  {product.name}
                                </p>
                                <p className="font-outfit text-xs text-primary">
                                  ${product.price?.toFixed(2)}
                                </p>
                              </div>
                            </button>
                          ))}
                          {searchQuery.trim() && (
                            <button
                              data-testid="search-view-all"
                              onClick={() => {
                                setShowSearch(false);
                                setSearchResults([]);
                                navigate(`/shop?search=${encodeURIComponent(searchQuery.trim())}`);
                              }}
                              className="w-full px-4 py-3 text-center text-sm text-primary hover:bg-muted transition-colors font-outfit"
                            >
                              View all results →
                            </button>
                          )}
                        </div>
                      ) : searchQuery.trim() ? (
                        <div className="p-4 text-center text-muted-foreground font-outfit text-sm">
                          No products found
                        </div>
                      ) : (
                        <div className="p-4 text-center text-muted-foreground font-outfit text-sm">
                          Start typing to search...
                        </div>
                      )}
                    </div>

                    {/* Order Lookup Link */}
                    <div className="border-t border-border p-3">
                      <Link
                        to="/track-order"
                        data-testid="search-track-order"
                        onClick={() => setShowSearch(false)}
                        className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors font-outfit"
                      >
                        <Package className="w-4 h-4" />
                        Track your order
                      </Link>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <Button
              data-testid="cart-btn"
              variant="ghost"
              size="icon"
              onClick={onCartClick}
              className="relative hover:bg-muted"
            >
              <ShoppingCart className="w-5 h-5 text-foreground" />
              {cartItemCount > 0 && (
                <Badge
                  data-testid="cart-badge"
                  className="absolute -top-1 -right-1 w-5 h-5 flex items-center justify-center p-0 bg-accent text-accent-foreground text-xs font-bold"
                >
                  {cartItemCount}
                </Badge>
              )}
            </Button>

            {/* Mobile Menu Toggle */}
            <Button
              data-testid="mobile-menu-toggle"
              variant="ghost"
              size="icon"
              className="lg:hidden hover:bg-muted"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? (
                <X className="w-5 h-5 text-foreground" />
              ) : (
                <Menu className="w-5 h-5 text-foreground" />
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isMobileMenuOpen && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          exit={{ opacity: 0, height: 0 }}
          className="lg:hidden glass border-t border-border"
        >
          <nav className="flex flex-col py-4 px-4">
            <Link
              to="/shop"
              data-testid="mobile-nav-shop-all"
              onClick={() => setIsMobileMenuOpen(false)}
              className={`font-outfit py-3 text-sm tracking-wider uppercase transition-colors duration-300 ${
                location.pathname === "/shop"
                  ? "text-primary"
                  : "text-muted-foreground hover:text-primary"
              }`}
            >
              Shop All
            </Link>
            
            {/* Category Links */}
            <div className="border-t border-border mt-2 pt-2">
              <span className="font-outfit text-xs text-muted-foreground uppercase tracking-wider px-1">
                Categories
              </span>
              {categories.map((cat, idx) => (
                <Link
                  key={`mobile-${cat.franpos_id || cat.id || idx}-${cat.slug || ''}`}
                  to={`/shop/${cat.slug || cat.id}`}
                  data-testid={`mobile-nav-${cat.slug || cat.id}`}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`font-outfit py-3 pl-3 text-sm tracking-wider uppercase transition-colors duration-300 block ${
                    location.pathname === `/shop/${cat.slug || cat.id}`
                      ? "text-primary"
                      : "text-muted-foreground hover:text-primary"
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            {/* Static Links */}
            <div className="border-t border-border mt-2 pt-2">
              {staticLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`mobile-nav-${link.label.toLowerCase()}`}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`font-outfit py-3 text-sm tracking-wider uppercase transition-colors duration-300 block ${
                    location.pathname === link.path
                      ? "text-primary"
                      : "text-muted-foreground hover:text-primary"
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </nav>
        </motion.div>
      )}
    </motion.header>
  );
};

export default Header;
